<template>
  <pf-table v-if="filteredOrderedProducts.length" v-bind="ouiaProps" striped>
    <pf-thead>
      <pf-tr>
        <pf-th>
          <sort-link v-if="!hideDomainSort" v-model="s" v-model:reverse="r" field="dominio">
            {{ $t('Dominio') }}
          </sort-link>
          <template v-else>{{ $t('Dominio') }}</template>
        </pf-th>
        <pf-th>
          <sort-link v-model="s" v-model:reverse="r" field="nome">
            {{ $t('Nome') }}
          </sort-link>
        </pf-th>
        <pf-th class="price">
          <sort-link v-model="s" v-model:reverse="r" field="prezzo">
            {{ $t('Importo') }}
          </sort-link>
        </pf-th>
        <pf-th>{{ $t('Durata') }}</pf-th>
        <pf-th style="min-width: 130px;">
          <sort-link v-model="s" v-model:reverse="r" field="data_fine">
            {{ $t('Scadenza') }}
          </sort-link>
        </pf-th>
        <pf-th style="min-width: 190px;">
          {{ $t('Rinnovo') }}
        </pf-th>
        <pf-th style="max-width: 240px" class="pf-v6-u-text-align-end">
          {{ $t('Azioni') }}
        </pf-th>
      </pf-tr>
    </pf-thead>
    <pf-tbody>
      <pf-tr v-for="(product, idx) in filteredOrderedProducts" :key="product.id">
        <pf-td :data-label="$t('Dominio')">{{ product.dominio }}</pf-td>
        <pf-td :data-label="$t('Nome')">
          {{ product.nome }}<span v-if="product.qt > 1"> (x{{ product.qt }})</span>{{ ' ' }} <pf-label v-if="product.nfr" color="green" :title="$t('Prodotto not for resale')">NFR</pf-label>
          <template v-if="product.note_cliente">
            <br>
            {{ $t('Note') }}: {{ product.note_cliente }}
          </template>
          <template v-if="product.last_bw_upgrade">
            <br>
            <i>{{ $t('Upgrade di traffico automatico (in data {0})', [$date(product.last_bw_upgrade)]) }} - <template v-if="product.free_bw_upgrade">{{ $t('Primo mese gratuito') }}</template></i>
          </template>
        </pf-td>
        <pf-td :data-label="$t('Importo')" class="price">
          {{ $currency(product.prezzo, login.currency) }}
          <template v-if="product.last_bw_upgrade">
            <br>
            <i v-if="product.free_bw_upgrade">{{ $currency(0, login.currency) }}</i>
            <i v-else>{{ $currency(product.bw_upgrade_price, login.currency) }}</i>
          </template>
        </pf-td>
        <pf-td :data-label="$t('Durata')">
          {{
            product.durata == 'Annuale'
              ? $t('Annuale')
              : product.durata == 'Mensile'
                ? $t('Mensile')
                : $t('Una Tantum')
          }}
        </pf-td>
        <pf-td :data-label="$t('Scadenza')" style="min-width: 160px;">
          {{ $date(product.data_fine) }}
          <pf-content component="small">{{ daysFromNow(product.data_fine) }}</pf-content>
        </pf-td>
        <pf-td :data-label="$t('Rinnovo')">
          <template v-if="product.free_period > 0">
            <span v-if="product.days_to_expiration > 0" :class="renewClass(product.disdetto || !product.rinnovo_automatico)">
              {{ product.disdetto || !product.rinnovo_automatico? $t('Prodotto in prova in cancellazione al termine del test') : $t('Prodotto in prova che verrà rinnovato al termine del test') }}
            </span>
            <span v-else :class="renewClass(product.disdetto || !product.rinnovo_automatico)">
              {{ product.disdetto || !product.rinnovo_automatico? $t('Periodo di prova terminato. Prodotto in cancellazione') : $t('Periodo di prova terminato. Prodotto in fatturazione') }}
            </span>
          </template>

          <template v-else-if="product.days_to_expiration < 0">
            <span :class="renewClass(product.disdetto || !product.rinnovo_automatico)">{{ product.disdetto || !product.rinnovo_automatico ? $t('Prodotto scaduto in cancellazione') : $t('Prodotto in fase di rinnovo') }}</span>
          </template>

          <template v-else-if="product.disdetto">
            <span class="pf-v6-u-text-color-status-danger">{{ $t('Disdetto') }}</span>
          </template>

          <template v-else-if="product.pending_invoices !== null">
            <span class="pf-v6-u-text-color-status-danger">{{ $t('Pagamenti arretrati') }}</span>
          </template>
          <template v-else>
            <template v-if="product.durata === 'Una tantum'">
              {{ $t('Una tantum') }}
            </template>

            <template v-else-if="login.fatturante.strid === 'BBW'">
              <pf-switch
                :disabled="!login.grants.billing"
                v-model:checked="product.rinnovo_automatico"
                :label="product.rinnovo_automatico ? $t('Automatico') : $t('Su richiesta')"
                value="1"
                off-value="0"
                @update:checked="setChangingProduct(idx, 'autorenew')"
              />
              <form-help-tooltip
                v-if="!login.grants.billing"
                :text="$t('Non hai i permessi per modificare la tipologia di rinnovo del prodotto. Richiedili ad un utente amministratore.')"
              />
            </template>

            <span
              v-else
              :class="renewClass(product.disdetto || !product.rinnovo_automatico)">
                {{ product.disdetto || !product.rinnovo_automatico ? $t('Non rinnovato alla scadenza') : $t('Rinnovo automatico') }}
            </span>
          </template>
        </pf-td>
        <pf-td class="pf-v6-u-text-align-end">
          <pf-flex gap="xs" justify-content="flex-end">
            <granted-link
              v-if="product.pending_invoices !== null"
              :to="{ name: 'invoices' }"
              :help-message="$t('La fattura nr. {0} che include il rinnovo di questo prodotto non è ancora stata saldata', [
                  product.pending_invoices,
                ])"
              type="danger"
              small
              tooltip-position="left"
            >
              {{ $t('Vai alla sezione fatture') }}
            </granted-link>

            <template v-else>
              <template v-if="product.durata != 'Una tantum'">
                <pf-tooltip v-if="product.grace_period && product.disdetto" position="left">
                  <granted-link
                    :to="{ name: 'product_renew', params: { id: product.id } }"
                    disabled-message="Non hai i permessi per riattivare il prodotto. Richiedili ad un utente amministratore."
                    type="primary"
                    small
                    tooltip-position="left"
                  >
                    <template #icon><triangle-exclamation-icon /></template>
                    {{ $t('Riattiva') }}
                  </granted-link>
                  <template #content>{{ $t('Clicca per procedere alla riattivazione di questo prodotto, i giorni rimasti per poter procedere sono {0}', [product.grace_period]) }}</template>
                </pf-tooltip>

                <template v-else-if="product.days_to_expiration >= 0">
                  <pf-button
                    v-if="product.rinnovo_automatico"
                    :variant="product.disdetto ? 'primary' : 'danger'"
                    small
                    icon-position="end"
                    :disabled="!login.grants.billing"
                    @click.prevent="setChangingProduct(idx, 'state', product.disdetto)"
                  >
                    {{ product.disdetto ? $t('Rinnova') : $t('Disdici') }}

                    <template #icon v-if="!login.grants.billing">
                      <form-help-tooltip
                        position="left"
                        :text="$t('Non hai i permessi per disdire il prodotto. Richiedili ad un utente amministratore.')"
                      />
                    </template>
                  </pf-button>

                  <granted-link
                    v-else
                    :to="{ name: 'product_renew', params: { id: product.id } }"
                    disabled-message="Non hai i permessi per rinnovare il prodotto. Richiedili ad un utente amministratore."
                    type="primary"
                    small
                    tooltip-position="left"
                  >
                    {{ $t('Rinnova') }}
                  </granted-link>
                </template>

                <pf-tooltip v-else position="left">
                  <granted-link
                    :to="{ name: 'ticket', params: { id: 'new' } }"
                    small
                    type="primary"
                  >
                    <template #icon><ticket-icon /></template>
                  </granted-link>
                  <template #content>{{ $t('La data di rinnovo è passata, non è più possibile effettuare modifiche. Clicca qui per aprire un ticket al supporto e verificare la situazione.') }}</template>
                </pf-tooltip>

                <granted-link
                  v-if="product.n_upgrades && (product.days_to_expiration >= 0 || (!product.disdetto && product.rinnovo_automatico))"
                  :to="{ name: 'product_upgrade', params: { id: product.id } }"
                  type="primary"
                  small
                  disabled-message="Non hai i permessi per effettuare l'upgrade del prodotto"
                  :title="$t('Effettua upgrade del prodotto')"
                  tooltip-position="left"
                >
                  {{ $t('Upgrade') }}
                </granted-link>
              </template>

              <pf-tooltip position="left">
                <pf-button small variant="secondary" @click="productNotePopup = product">
                  <template #icon><comment-icon /></template>
                </pf-button>
                <template #content>{{ $t('Assegna una nota al prodotto che sarà visibile in fattura') }}</template>
              </pf-tooltip>
            </template>
          </pf-flex>
        </pf-td>
      </pf-tr>
    </pf-tbody>
  </pf-table>

  <pf-empty-state v-else variant="xs">
    <pf-empty-state-header :title="$t('Nessun prodotto trovato con il filtro selezionato.')">
      <template #icon>
        <pf-empty-state-icon>
          <magnifying-glass-icon />
        </pf-empty-state-icon>
      </template>
    </pf-empty-state-header>
  </pf-empty-state>

  <prodotti-popup-note
    v-if="productNotePopup"
    v-model:customer-note="productNotePopup.note_cliente"
    :product-id="productNotePopup.id"
    @close="productNotePopup = null"
    @submit="submit"
  />

  <prodotti-popup-renewal-change
    v-if="changingProduct !== null"
    :product="filteredOrderedProducts[changingProduct.product]"
    :variant="changingProduct.variant"
    v-model:renew="filteredOrderedProducts[changingProduct.product].rinnovo_automatico"
    @submit="submit"
    @close="setChangingProduct(null)"
  />
</template>

<script lang="ts" setup>
import { reactive, ref, watch, type Reactive, type Ref } from 'vue';
import { any, by } from '@common/utils';
import { type ProductOrder, type ProductsExpiringPayments } from '@/resources';
import ProdottiPopupNote from '../components/prodotti-popup-note.vue';
import ProdottiPopupRenewalChange from '../components/prodotti-popup-renewal-change.vue';
import TriangleExclamationIcon from '@vue-patternfly/icons/triangle-exclamation-icon';
import TicketIcon from '@vue-patternfly/icons/ticket-icon';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import SortLink from '../components/sort-link.vue';
import GrantedLink from '../components/granted-link.vue';
import MagnifyingGlassIcon from '@vue-patternfly/icons/magnifying-glass-icon';
import CommentIcon from '@vue-patternfly/icons/comment-icon';
import { daysFromNow } from '@common/date';
import { useLoginStore } from '@/store/login';
import { PfEmptyState } from '@vue-patternfly/core';
import { isDefined } from '@vueuse/core';
import { useOUIAProps, type OUIAProps } from '@common/ouia';

const props = defineProps<OUIAProps & {
  products: ProductOrder[];
  payments?: Record<number, ProductsExpiringPayments>;
  hideDomainSort?: Boolean;
  filter?: string;
}>();
const ouiaProps = useOUIAProps(props);

const emit = defineEmits<{
  (name: 'submit'): void;
}>();

const login = useLoginStore();

const changingProduct: Ref<{
  product: number;
  variant: 'state' | 'autorenew';
} | null> = ref(null);

const s: Ref<keyof ProductOrder> = ref('data_fine'); // sort by
const r = ref(false); // reverse sort

const filteredOrderedProducts: Reactive<(ProductOrder & { pending_invoices: string[] | null })[]> = reactive([]);

watch(() => [props.filter, props.payments], () => {
  const products = props.filter ? props.products.filter(any(props.filter)) : props.products;
  changingProduct.value = null;
  filteredOrderedProducts.length = 0;

  filteredOrderedProducts.push(...products
    .sort(by(p => {
      if (s.value === 'prezzo') {
        return Number(p.prezzo);
      }
      return p[s.value];
    }, r.value))
    .map(p => {
      return {
        ...p,
        pending_invoices: props.payments?.[p.id] ?? null,
      };
    }));
}, { immediate: true });

const productNotePopup: Ref<ProductOrder | null> = ref(null);

function renewClass(disdetto: boolean) {
  return disdetto ? 'pf-v6-u-text-color-status-danger' : 'pf-v6-u-text-color-status-success';
}

function setChangingProduct(productIdx: number|null, variant?: 'state'|'autorenew', renew?: boolean) {
  if (productIdx === null || !filteredOrderedProducts[productIdx]) {
    changingProduct.value = null;
    return;
  }

  if (isDefined(renew)) {
    filteredOrderedProducts[productIdx].rinnovo_automatico = renew;
  }

  changingProduct.value = {
    product: productIdx,
    variant: variant ?? 'state',
  };
}

function submit() {
  emit('submit')
}
</script>
